import {apiRequest, ApiErrorKind} from "@/tools/request/v2";
import {calculateCPM} from "@/tools/acrobatics";
import {groupData, sumArray} from "@/tools/reportChartTools";

export const ReportRequestStatus = Object.freeze({
    IDLE: "idle",
    LOADING: "loading",
    LOADED: "loaded",
    ERROR: "error",
})

// Only the response of the most recent report request is applied
let latestReportRequestId = 0

const reports = {
    state: () => ({
        metadata: null,
        report: null,
        reportStatus: ReportRequestStatus.IDLE,
        reportErrorKind: null,
        lastReportRequest: null,
        subscriptionError: null, // null | "create" | "update"
        historyToFetch: null,
        subscriptions: [],
        reportChartLoaded: false
    }),
    mutations: {
        setReport(state, payload) {
            Object.freeze(payload)
            state.report = payload
        },
        setReportStatus(state, {status, errorKind = null}) {
            state.reportStatus = status
            state.reportErrorKind = errorKind
        },
        setLastReportRequest(state, payload) {
            state.lastReportRequest = payload
        },
        setSubscriptionError(state, payload) {
            state.subscriptionError = payload
        },
        clearReport(state) {
            state.report = null
        },
        setMetadata(state, payload) {
            state.metadata = payload
        },
        setHistoryToFetch(state, payload) {
            state.historyToFetch = payload
        },
        setSubscriptions(state, payload) {
            state.subscriptions = payload
        }

    },
    actions: {
        fetchMetadata(context) {
            // Users without report access get an error response here; metadata then
            // stays null, which is what userHasReportAccess checks. Not an error to show.
            return apiRequest('get', "reports", "", true).then(result => {
                if (result.ok && result.body && result.body.data) {
                    context.commit("setMetadata", result.body.data)
                }
            })
        },
        fetchAPIReport(context, data) {
            const requestId = ++latestReportRequestId
            context.commit("setLastReportRequest", data)
            context.commit("clearReport")
            context.commit("setReportStatus", {status: ReportRequestStatus.LOADING})

            return apiRequest('post', "reports", data, true).then(result => {
                if (requestId !== latestReportRequestId) return

                if (result.ok && result.body && result.body.data) {
                    context.commit("setReport", result.body.data)
                    context.commit("setReportStatus", {status: ReportRequestStatus.LOADED})
                } else {
                    console.error("report request failed", result)
                    context.commit("setReportStatus", {
                        status: ReportRequestStatus.ERROR,
                        errorKind: result.kind || ApiErrorKind.HTTP,
                    })
                }
            })
        },
        retryAPIReport(context) {
            if (context.state.lastReportRequest) {
                return context.dispatch("fetchAPIReport", context.state.lastReportRequest)
            }
        },
        // Resolves to true on success so callers can react to failures
        createSubscription(context, data) {
            context.commit("setSubscriptionError", null)
            return apiRequest('post', "reports/subscriptions", data, true).then(result => {
                if (result.ok && result.body && result.body.data) {
                    context.dispatch("fetchSubscriptions")
                    return true
                }
                context.commit("setSubscriptionError", "create")
                return false
            })
        },
        fetchSubscriptions(context) {
            return apiRequest('get', "reports/subscriptions", "", true).then(result => {
                if (result.ok && result.body && result.body.data) {
                    context.commit("setSubscriptions", result.body.data)
                }
            })
        },
        // Resolves to true on success. On failure the list is reloaded so that
        // optimistic edits made to the subscription object are rolled back.
        updateSubscription(context, subscription) {
            context.commit("setSubscriptionError", null)
            return apiRequest('put', `reports/subscriptions`, subscription, true).then(result => {
                context.dispatch("fetchSubscriptions")
                if (result.ok && result.body && result.body.data) {
                    return true
                }
                context.commit("setSubscriptionError", "update")
                return false
            })
        },
    },
    getters: {
        getReport(state) {
            return state.report
        },
        getMetadata(state) {
            return state.metadata
        },
        getReportStatus(state) {
            return state.reportStatus
        },
        getReportErrorKind(state) {
            return state.reportErrorKind
        },
        getSubscriptionError(state) {
            return state.subscriptionError
        },
        userHasReportAccess(state) {
            return state.metadata !== null
        },
        getHistoryToFetch(state) {
            return state.historyToFetch
        },
        getSubscriptions(state) {
            return state.subscriptions
        },
        isReportChartLoaded: (state, getters) => {
            return state.reportChartLoaded
        },
        getDimensionsForCurrentReport: (state, getters) => {
            let report = getters.getReport
            if (!report) return
            const indices = report[0]
            const templates = getters.getMetadata.templates
            let allDimensions = new Set()
            for (const [key, value] of Object.entries(templates)) {
                for (const dim of value.dimensions) {
                    allDimensions.add(dim)
                }
            }
            return indices.filter(index => allDimensions.has(index.name)).map(index => index.name)
        },
        getMetricsForCurrentReport: (state, getters) => {
            let report = getters.getReport
            if (!report) return
            const indices = report[0]
            const templates = getters.getMetadata.templates
            let allMetrics = new Set()
            for (const [key, value] of Object.entries(templates)) {
                for (const metric of value.metrics) {
                    allMetrics.add(metric)
                }
            }
            return indices.filter(index => allMetrics.has(index.name)).map(index => index.name)
        },

        getReportChartData: (state, getters) => {
            console.time("report chart time")

            const report = getters.getReport;
            if (!report) return;

            const indices = report[0];
            const data = report.slice(1);

            const metrics = getters.getMetricsForCurrentReport
            const dimensions = getters.getDimensionsForCurrentReport

            const findHeaderIndex = (value) => {
                return indices.findIndex((e) => e.name === value)
            }

            const reportChartData = {
                date: [],
                values: []
            }


            // converting data as needed for chart
            for (const rowData of data) {
                if (!reportChartData.date.includes(rowData[0])) {
                    reportChartData.date.push(rowData[0])
                }

                const entryIndex = reportChartData.values.findIndex(entry => {
                    return dimensions.every(dimension => entry[dimension] === rowData[findHeaderIndex(dimension)])
                })


                if (entryIndex === -1) {
                    //not in the list
                    const newEntry = {}
                    for (const dimension of dimensions) {
                        newEntry[dimension] = rowData[findHeaderIndex(dimension)]
                    }
                    for (const metric of metrics) {
                        newEntry[metric] = {[rowData[0]]: rowData[findHeaderIndex(metric)]}
                    }
                    reportChartData.values.push(newEntry)
                } else {
                    for (const metric of metrics) {
                        reportChartData.values[entryIndex][metric][rowData[0]] = rowData[findHeaderIndex(metric)]
                    }
                }
            }

            //totaling each entries metric to sort them later by individual total
            for (const entry of reportChartData.values) {
                const tempMetrics = {}
                let localTotal = {}
                for (const metric of metrics) {
                    tempMetrics[metric] = [];
                    localTotal[metric] = 0
                }

                for (const currentDate of reportChartData.date) {
                    for (const metric of metrics) {
                        if (!entry[metric][currentDate]) {
                            tempMetrics[metric].push(0);
                        } else {
                            tempMetrics[metric].push(entry[metric][currentDate]);
                            localTotal[metric] += entry[metric][currentDate];
                        }
                    }
                }

                entry.total = {}
                for (const metric of metrics) {
                    entry[metric] = tempMetrics[metric];
                    entry.total[metric] = localTotal[metric];
                }
            }

            console.log("reportChartData pre", reportChartData)
            console.timeEnd("report chart time")
            return reportChartData

        },
    }
}

export default reports
